import { ReactNode, KeyboardEventHandler } from 'react';
import styled from 'styled-components';
import { Label } from 'components/Typography';
import get from 'lodash/get';
import { FormikProps } from 'formik';
import Input from '../Input';
import {
  Container,
  Optional,
  ErrorMessage,
  CheckIcon,
  StyledTooltip,
  Comment,
  NewInputStyle
} from './styled';

interface TextInputProps {
  formik?: FormikProps<any>;
  name: string;
  optional?: boolean;
  label?: string | ReactNode;
  comment?: string | ReactNode;
  tooltip?: string;
  placeholder?: string;
  variant?: string;
  children?: any;
  type?: string;
  spacing?: string;
  className?: string;
  onKeyDown?: KeyboardEventHandler;
  disabled?: boolean;
  inputMode?: string;
  validateWhileTyping?: boolean;
  size?: 'medium' | 'large' | 'extralarge';
  maxlength?: number;
}

const TextInput: React.FC<TextInputProps> = ({
  label = '',
  comment = '',
  tooltip = '',
  placeholder = '',
  optional = false,
  variant = 'dark',
  size = 'medium',
  children = [],
  type = 'text',
  spacing = 'normal',
  name,
  inputMode,
  disabled,
  onKeyDown,
  validateWhileTyping,
  className,
  maxlength,
  formik
}) => {
  const getInputProps = () => {
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { value: _, ...rest } = formik?.getFieldProps(name);
    const value = get(formik, `values.${name}`);

    return {
      placeholder,
      type,
      variant,
      spacing,
      inputMode,
      disabled,
      onKeyDown,
      value: typeof maxlength !== 'undefined' ? value.slice(0, maxlength) : value,
      ...rest
    };
  };

  const validateWhileTypingOrBlur = () =>
    // show validation errors after 5 characters have been typed
    // or user has clicked away from field
    (get(formik?.values, name) && get(formik?.values, name).length > 5) ||
    get(formik?.touched, name);

  const renderLabel = (
    <Label htmlFor={name} variant={variant} size={size} spacing={spacing}>
      {label}
      {tooltip && (
        <StyledTooltip variant={variant === 'block' ? 'light' : variant}>
          {tooltip}
        </StyledTooltip>
      )}
      {optional && <Optional>- optional</Optional>}
      {formik && !optional && (
        <>
          {(validateWhileTyping
            ? validateWhileTypingOrBlur()
            : get(formik.touched, name)) && get(formik.errors, name) ? (
            <ErrorMessage>
              <>{get(formik?.errors, name)}</>
            </ErrorMessage>
          ) : null}
          {!get(formik.errors, name) && get(formik.touched, name) && formik.dirty && (
            <CheckIcon />
          )}
        </>
      )}
      {comment && <Comment>{comment}</Comment>}
    </Label>
  );

  return (
    <Container size={size} className={className}>
      {label && renderLabel}
      {/* @ts-ignore */}
      <Input {...getInputProps()} />
      {children}
    </Container>
  );
};

export const NewTextInput = styled(TextInput)`
  ${NewInputStyle}
`;

export default TextInput;
